<script lang="ts">
	import { mutation } from "@api/graphql.js";
	import sendWebMessage from "@api/mutations/sendWebMessage.graphql";
	import Button from "@components/common/Button.svelte";
	import Link from "@components/common/Link.svelte";
	import Modal from "@components/common/Modal.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import { LinkVariant } from "@components/common/getLinkClass.js";
	import Form from "@components/form/Form.svelte";
	import LabelledInput from "@components/form/LabelledInput.svelte";
	import LabelledTextarea from "@components/form/LabelledTextarea.svelte";
	import type { WebMessageInput } from "@core/schema/WebMessage.js";
	import { WebMessageType } from "@core/schema/WebMessageType.js";
	import { getPrivacyPolicyPath } from "@core/schema/paths/getPrivacyPolicyPath.js";
	import type { Complete } from "@core/utils/Complete.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { productForContactForm } from "../state/productForContactForm.svelte.js";

	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();

	let showSuccessModal = $state<boolean>(false);
	let errorHappened = $state<boolean>(false);
	let gdprApprovedAt = $state<string | null>(null);

	function onGdprToggle(event: Event): void {
		const input = event.target as HTMLInputElement;
		gdprApprovedAt = input.checked ? new Date().toISOString() : null;
	}

	const send = createPromiseWithStatus(async (input: Omit<WebMessageInput, "contactDetail">) =>
		mutation<boolean, { input: typeof input }>(PUBLIC_GRAPHQL_ENDPOINT, sendWebMessage, { input }),
	);

	async function onsubmit(event: SubmitEvent): Promise<void> {
		event.preventDefault();
		if (!gdprApprovedAt) {
			throw new Error("GDPR must be approved.");
		}

		errorHappened = false;

		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);
		const name = formData.get("name") as string;
		const phoneNumber = formData.get("phoneNumber") as string;
		const email = formData.get("email") as string;
		const message = formData.get("message") as string;

		try {
			const input: Complete<WebMessageInput> = {
				name,
				phoneNumber,
				email,
				type: WebMessageType.Contact,
				gdprApprovedAt,
				contactDetail: {
					message,
					product: productForContactForm.current,
					webUrl: window.location.href,
				},
				locale: tenant.locale,
			};
			await send.call(input);
			showSuccessModal = true;
			form.reset();
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			errorHappened = true;
		}
	}
</script>

<Modal show={showSuccessModal}>
	<p class="text-xl text-fstonyx">Děkujeme za zprávu</p>
	<div class=" items-left flex flex-col gap-8">
		<p>
			Vaše zpráva byla úspěšně odeslána.
			<br />
			Brzy se vám ozveme.
		</p>

		<Button
			variant={ButtonVariant.Primary}
			class="ml-auto"
			onclick={(): void => {
				showSuccessModal = false;
			}}
		>
			Rozumím
		</Button>
	</div>
</Modal>

<div class="flex flex-col items-center gap-8" id="contact-form">
	<Form class="lg:w-[28.5rem] flex flex-col gap-2" {onsubmit}>
		<LabelledInput type="text" name="name" placeholder="Jakub Novák" required>Jméno a přijmení</LabelledInput>
		<LabelledInput type="phone" name="phoneNumber" placeholder="+420 721 712 555" required>Telefon</LabelledInput>
		<LabelledInput type="email" name="email" placeholder="email@google.com" required>E-mail</LabelledInput>
		<LabelledTextarea name="message">Zpráva</LabelledTextarea>
		<LabelledInput type="checkbox" name="gdprApproved" onchange={onGdprToggle} required>
			Souhlasím se
			<Link variant={LinkVariant.Underline} href={getPrivacyPolicyPath(tenant)}>
				Zásadami zpracování osobních údajů
			</Link>
		</LabelledInput>

		<Button variant={ButtonVariant.Primary} class="mx-auto" disabled={send.value.status === "loading"}>
			{#if send.value.status === "loading"}
				Odesílání…
			{:else}
				Odeslat
			{/if}
		</Button>
	</Form>

	{#if errorHappened}
		<p class="text-red-400">
			Omlouváme se, ale při odesílání formuláře došlo k chybě. Zkuste to prosím znovu nebo později.
		</p>
	{/if}
</div>
